// card #3200 - a tiny in-memory pub/sub, same shape as sheetContentEvents.js
// right next to it. The change check (changeCheck.js) and the hydrate walk
// write a class's freshly pulled ride rows into IndexedDB; an OPEN ride sheet
// (useRideNoShow / useRideExcused / useRideWithdrawn / useRideUtilityEvents)
// has no other way to hear that, now that the per-ride 4s polls are gone. The
// payload carries the rows themselves, so a subscriber reads its ride's
// ride-level facts straight off the pulled row with NO further request.
//
// Never persists across a reload: a reload re-reads everything from scratch.
const listeners = new Set();

/**
 * @param {string} classId
 * @param {Array<object>} rideRows the rows just written to the rides cache
 * @param {{ startedAt?: number }} [meta] startedAt: epoch ms the ridesByClass
 *   fetch BEGAN. A subscriber that has just settled its own write drops a pull
 *   that began before it: that pull's rows may predate the write, and
 *   re-applying them would blank what the write just put on screen.
 */
export function emitClassRidesPulled(classId, rideRows, meta = {}) {
  if (listeners.size === 0) return;
  // Iterate a copy: a listener unsubscribing mid-callback (a hook unmounting in
  // response) must not skip a sibling.
  for (const fn of [...listeners]) {
    try {
      fn({ classId, rides: rideRows ?? [], startedAt: meta.startedAt ?? null });
    } catch {
      // one subscriber's error must not stop the others hearing the pull
    }
  }
}

/** @returns {() => void} unsubscribe */
export function subscribeToClassRidesPulled(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// Test-only: the registry is module state.
export function _resetRideCacheEventsForTests() {
  listeners.clear();
}

// Test-only: how many subscribers are registered (proves an unmount unsubscribes).
export function _listenerCountForTests() {
  return listeners.size;
}
